/**
 * @fileoverview
 * Sistema de registro de blocos customizados para extensões.
 * Quando uma extensão customizada é carregada, registra seus blocos no ScratchBlocks.
 */

import ArgumentType from 'openblock-vm/src/extension-support/argument-type';
import BlockType from 'openblock-vm/src/extension-support/block-type';

/**
 * Registra blocos de uma extensão customizada no ScratchBlocks.
 * @param {object} ScratchBlocks - Instância do ScratchBlocks
 * @param {string} extensionId - ID da extensão
 */
export const registerCustomExtensionBlocks = (ScratchBlocks, extensionId) => {
    if (!ScratchBlocks || !ScratchBlocks.Blocks) return;

    const categoryId = extensionId;
    const categoryColor = '#4C97FF';
    const categorySecondaryColor = '#3373CC';

    // Define blocos baseado na extensão
    const blocksToRegister = [];
    
    switch (extensionId) {
    case 'photoResistorSensor': {
        // Bloco: ler valor do fotoresistor
        const blockJson = {
            type: `${categoryId}_readValue`,
            message0: 'ler valor do fotoresistor pino %1',
            args0: [{
                type: 'field_dropdown',
                name: 'PIN',
                options: [['A0', 'A0'], ['A1', 'A1'], ['A2', 'A2'], ['A3', 'A3'], ['A4', 'A4'], ['A5', 'A5']]
            }],
            output: 'Number',
            colour: categoryColor,
            colourSecondary: categorySecondaryColor,
            outputShape: ScratchBlocks.OUTPUT_SHAPE_ROUND
        };
        blocksToRegister.push(blockJson);
        break;
    }
    case 'ultrasonicSensor': {
        // Bloco: ler distância sensor ultrassônico
        const blockJson = {
            type: `${categoryId}_readDistance`,
            message0: 'ler distância sensor ultrassônico trigger %1 echo %2',
            args0: [
                {
                    type: 'field_dropdown',
                    name: 'TRIGGER',
                    options: [['2', '2'], ['3', '3'], ['4', '4'], ['5', '5'], ['6', '6'], ['7', '7'], ['8', '8'], ['9', '9'], ['10', '10'], ['11', '11'], ['12', '12'], ['13', '13']]
                },
                {
                    type: 'field_dropdown',
                    name: 'ECHO',
                    options: [['2', '2'], ['3', '3'], ['4', '4'], ['5', '5'], ['6', '6'], ['7', '7'], ['8', '8'], ['9', '9'], ['10', '10'], ['11', '11'], ['12', '12'], ['13', '13']]
                }
            ],
            output: 'Number',
            colour: categoryColor,
            colourSecondary: categorySecondaryColor,
            outputShape: ScratchBlocks.OUTPUT_SHAPE_ROUND
        };
        blocksToRegister.push(blockJson);
        break;
    }
    case 'linetracingIRSensor': {
        // Bloco: ler valor sensor IR
        const blockJson1 = {
            type: `${categoryId}_readValue`,
            message0: 'ler valor sensor IR rastreamento de linha pino %1',
            args0: [{
                type: 'field_dropdown',
                name: 'PIN',
                options: [['A0', 'A0'], ['A1', 'A1'], ['A2', 'A2'], ['A3', 'A3'], ['A4', 'A4'], ['A5', 'A5']]
            }],
            output: 'Number',
            colour: categoryColor,
            colourSecondary: categorySecondaryColor,
            outputShape: ScratchBlocks.OUTPUT_SHAPE_ROUND
        };
        ScratchBlocks.Blocks[`${categoryId}_readValue`] = {
            init: function () {
                this.jsonInit(blockJson1);
            }
        };

        // Bloco: sensor IR detecta linha preta
        const blockJson2 = {
            type: `${categoryId}_isBlack`,
            message0: 'sensor IR detecta linha preta pino %1',
            args0: [{
                type: 'field_dropdown',
                name: 'PIN',
                options: [['A0', 'A0'], ['A1', 'A1'], ['A2', 'A2'], ['A3', 'A3'], ['A4', 'A4'], ['A5', 'A5']]
            }],
            output: 'Boolean',
            colour: categoryColor,
            colourSecondary: categorySecondaryColor,
            outputShape: ScratchBlocks.OUTPUT_SHAPE_HEXAGONAL
        };
        ScratchBlocks.Blocks[`${categoryId}_isBlack`] = {
            init: function () {
                this.jsonInit(blockJson2);
            }
        };
        break;
    }
    case 'dht11Sensor': {
        // Bloco: ler temperatura DHT11
        const blockJson1 = {
            type: `${categoryId}_readTemperature`,
            message0: 'ler temperatura DHT11 pino %1',
            args0: [{
                type: 'field_dropdown',
                name: 'PIN',
                options: [['2', '2'], ['3', '3'], ['4', '4'], ['5', '5'], ['6', '6'], ['7', '7'], ['8', '8'], ['9', '9'], ['10', '10'], ['11', '11'], ['12', '12'], ['13', '13']]
            }],
            output: 'Number',
            colour: categoryColor,
            colourSecondary: categorySecondaryColor,
            outputShape: ScratchBlocks.OUTPUT_SHAPE_ROUND
        };
        blocksToRegister.push(blockJson1);

        // Bloco: ler umidade DHT11
        const blockJson2 = {
            type: `${categoryId}_readHumidity`,
            message0: 'ler umidade DHT11 pino %1',
            args0: [{
                type: 'field_dropdown',
                name: 'PIN',
                options: [['2', '2'], ['3', '3'], ['4', '4'], ['5', '5'], ['6', '6'], ['7', '7'], ['8', '8'], ['9', '9'], ['10', '10'], ['11', '11'], ['12', '12'], ['13', '13']]
            }],
            output: 'Number',
            colour: categoryColor,
            colourSecondary: categorySecondaryColor,
            outputShape: ScratchBlocks.OUTPUT_SHAPE_ROUND
        };
        blocksToRegister.push(blockJson2);
        break;
    }
    case 'liquidCrystalI2c': {
        // Bloco: inicializar LCD I2C
        const blockInit = {
            type: `${categoryId}_init`,
            message0: 'inicializar LCD I2C endereço %1 colunas %2 linhas %3',
            args0: [
                {
                    type: 'field_dropdown',
                    name: 'ADDR',
                    options: [['0x27', '0x27'], ['0x3F', '0x3F'], ['0x20', '0x20'], ['0x38', '0x38']]
                },
                {
                    type: 'field_dropdown',
                    name: 'COLS',
                    options: [['16', '16'], ['20', '20']]
                },
                {
                    type: 'field_dropdown',
                    name: 'ROWS',
                    options: [['2', '2'], ['4', '4']]
                }
            ],
            previousStatement: null,
            nextStatement: null,
            colour: categoryColor,
            colourSecondary: categorySecondaryColor
        };
        blocksToRegister.push(blockInit);

        // Bloco: escrever no LCD
        const blockPrint = {
            type: `${categoryId}_printAt`,
            message0: 'escrever no LCD endereço %1 linha %2 coluna %3 texto %4',
            args0: [
                {
                    type: 'field_dropdown',
                    name: 'ADDR',
                    options: [['0x27', '0x27'], ['0x3F', '0x3F'], ['0x20', '0x20'], ['0x38', '0x38']]
                },
                {
                    type: 'field_dropdown',
                    name: 'ROW',
                    options: [['0', '0'], ['1', '1']]
                },
                {
                    type: 'field_dropdown',
                    name: 'COL',
                    options: [['0', '0'], ['1', '1'], ['2', '2'], ['3', '3'], ['4', '4'], ['5', '5'], ['6', '6'], ['7', '7'], ['8', '8'], ['9', '9'], ['10', '10'], ['11', '11'], ['12', '12'], ['13', '13'], ['14', '14'], ['15', '15']]
                },
                {
                    type: 'input_value',
                    name: 'TEXT',
                    check: ['String', 'Number']
                }
            ],
            previousStatement: null,
            nextStatement: null,
            colour: categoryColor,
            colourSecondary: categorySecondaryColor
        };
        blocksToRegister.push(blockPrint);

        // Bloco: limpar LCD
        const blockClear = {
            type: `${categoryId}_clear`,
            message0: 'limpar LCD endereço %1',
            args0: [
                {
                    type: 'field_dropdown',
                    name: 'ADDR',
                    options: [['0x27', '0x27'], ['0x3F', '0x3F'], ['0x20', '0x20'], ['0x38', '0x38']]
                }
            ],
            previousStatement: null,
            nextStatement: null,
            colour: categoryColor,
            colourSecondary: categorySecondaryColor
        };
        blocksToRegister.push(blockClear);
        break;
    }
    case 'tb6612fng': {
        const pinOpts = [['2', '2'], ['3', '3'], ['4', '4'], ['5', '5'], ['6', '6'], ['7', '7'], ['8', '8'], ['9', '9'], ['10', '10'], ['11', '11'], ['12', '12'], ['13', '13']];
        // Bloco: configurar ponte H TB6612
        const blockInit = {
            type: `${categoryId}_init`,
            message0: 'configurar ponte H TB6612 PWMA %1 AIN1 %2 AIN2 %3 PWMB %4 BIN1 %5 BIN2 %6',
            args0: [
                { type: 'field_dropdown', name: 'PWMA', options: pinOpts },
                { type: 'field_dropdown', name: 'AIN1', options: pinOpts },
                { type: 'field_dropdown', name: 'AIN2', options: pinOpts },
                { type: 'field_dropdown', name: 'PWMB', options: pinOpts },
                { type: 'field_dropdown', name: 'BIN1', options: pinOpts },
                { type: 'field_dropdown', name: 'BIN2', options: pinOpts }
            ],
            previousStatement: null,
            nextStatement: null,
            colour: categoryColor,
            colourSecondary: categorySecondaryColor
        };
        blocksToRegister.push(blockInit);

        // Bloco: Motor A direção e velocidade
        const blockMotorA = {
            type: `${categoryId}_motorA`,
            message0: 'Motor A %1 velocidade %2',
            args0: [
                {
                    type: 'field_dropdown',
                    name: 'DIR',
                    options: [['para frente', 'forward'], ['para trás', 'backward'], ['parar', 'stop']]
                },
                {
                    type: 'field_number',
                    name: 'SPEED',
                    value: 200,
                    min: 0,
                    max: 255
                }
            ],
            previousStatement: null,
            nextStatement: null,
            colour: categoryColor,
            colourSecondary: categorySecondaryColor
        };
        blocksToRegister.push(blockMotorA);

        // Bloco: Motor B direção e velocidade
        const blockMotorB = {
            type: `${categoryId}_motorB`,
            message0: 'Motor B %1 velocidade %2',
            args0: [
                {
                    type: 'field_dropdown',
                    name: 'DIR',
                    options: [['para frente', 'forward'], ['para trás', 'backward'], ['parar', 'stop']]
                },
                {
                    type: 'field_number',
                    name: 'SPEED',
                    value: 200,
                    min: 0,
                    max: 255
                }
            ],
            previousStatement: null,
            nextStatement: null,
            colour: categoryColor,
            colourSecondary: categorySecondaryColor
        };
        blocksToRegister.push(blockMotorB);
        break;
    }
    case 'l298n': {
        const pinOpts = [['2', '2'], ['3', '3'], ['4', '4'], ['5', '5'], ['6', '6'], ['7', '7'], ['8', '8'], ['9', '9'], ['10', '10'], ['11', '11'], ['12', '12'], ['13', '13']];
        const blockInit = {
            type: `${categoryId}_init`,
            message0: 'configurar ponte H L298N ENA %1 IN1 %2 IN2 %3 ENB %4 IN3 %5 IN4 %6',
            args0: [
                { type: 'field_dropdown', name: 'ENA', options: pinOpts },
                { type: 'field_dropdown', name: 'IN1', options: pinOpts },
                { type: 'field_dropdown', name: 'IN2', options: pinOpts },
                { type: 'field_dropdown', name: 'ENB', options: pinOpts },
                { type: 'field_dropdown', name: 'IN3', options: pinOpts },
                { type: 'field_dropdown', name: 'IN4', options: pinOpts }
            ],
            previousStatement: null,
            nextStatement: null,
            colour: categoryColor,
            colourSecondary: categorySecondaryColor
        };
        blocksToRegister.push(blockInit);

        const blockMotorA = {
            type: `${categoryId}_motorA`,
            message0: 'Motor A %1 velocidade %2',
            args0: [
                {
                    type: 'field_dropdown',
                    name: 'DIR',
                    options: [['para frente', 'forward'], ['para trás', 'backward'], ['parar', 'stop']]
                },
                {
                    type: 'field_number',
                    name: 'SPEED',
                    value: 200,
                    min: 0,
                    max: 255
                }
            ],
            previousStatement: null,
            nextStatement: null,
            colour: categoryColor,
            colourSecondary: categorySecondaryColor
        };
        blocksToRegister.push(blockMotorA);

        const blockMotorB = {
            type: `${categoryId}_motorB`,
            message0: 'Motor B %1 velocidade %2',
            args0: [
                {
                    type: 'field_dropdown',
                    name: 'DIR',
                    options: [['para frente', 'forward'], ['para trás', 'backward'], ['parar', 'stop']]
                },
                {
                    type: 'field_number',
                    name: 'SPEED',
                    value: 200,
                    min: 0,
                    max: 255
                }
            ],
            previousStatement: null,
            nextStatement: null,
            colour: categoryColor,
            colourSecondary: categorySecondaryColor
        };
        blocksToRegister.push(blockMotorB);
        break;
    }
    case 'tcrt5000Digital': {
        const blockJson = {
            type: `${categoryId}_readValue`,
            message0: 'valor digital TCRT5000 pino %1',
            args0: [{
                type: 'field_dropdown',
                name: 'PIN',
                options: [['2', '2'], ['3', '3'], ['4', '4'], ['5', '5'], ['6', '6'], ['7', '7'], ['8', '8'], ['9', '9'], ['10', '10'], ['11', '11'], ['12', '12'], ['13', '13']]
            }],
            output: 'Boolean',
            colour: categoryColor,
            colourSecondary: categorySecondaryColor,
            outputShape: ScratchBlocks.OUTPUT_SHAPE_HEXAGONAL
        };
        blocksToRegister.push(blockJson);
        break;
    }
    case 'mpu6050': {
        const addrOpts = [['0x68', '0x68'], ['0x69', '0x69']];
        const blockInit = {
            type: `${categoryId}_init`,
            message0: 'inicializar MPU-6050 endereço I2C %1',
            args0: [{ type: 'field_dropdown', name: 'ADDR', options: addrOpts }],
            previousStatement: null,
            nextStatement: null,
            colour: categoryColor,
            colourSecondary: categorySecondaryColor
        };
        blocksToRegister.push(blockInit);

        const reporter = (type, message, outputShape) => ({
            type: `${categoryId}_${type}`,
            message0: message,
            output: 'Number',
            colour: categoryColor,
            colourSecondary: categorySecondaryColor,
            outputShape: outputShape || ScratchBlocks.OUTPUT_SHAPE_ROUND
        });

        blocksToRegister.push(reporter('readAccelX', 'aceleração X (m/s²) MPU-6050'));
        blocksToRegister.push(reporter('readAccelY', 'aceleração Y (m/s²) MPU-6050'));
        blocksToRegister.push(reporter('readAccelZ', 'aceleração Z (m/s²) MPU-6050'));
        blocksToRegister.push(reporter('readGyroX', 'giroscópio X (rad/s) MPU-6050'));
        blocksToRegister.push(reporter('readGyroY', 'giroscópio Y (rad/s) MPU-6050'));
        blocksToRegister.push(reporter('readGyroZ', 'giroscópio Z (rad/s) MPU-6050'));
        blocksToRegister.push(reporter('readTemperature', 'temperatura (°C) MPU-6050'));
        break;
    }
    }
    
    // Registra todos os blocos usando defineBlocksWithJsonArray se disponível
    if (blocksToRegister.length > 0) {
        if (ScratchBlocks.defineBlocksWithJsonArray) {
            ScratchBlocks.defineBlocksWithJsonArray(blocksToRegister);
        } else {
            // Fallback: registra manualmente
            blocksToRegister.forEach(blockJson => {
                ScratchBlocks.Blocks[blockJson.type] = {
                    init: function () {
                        this.jsonInit(blockJson);
                    }
                };
            });
        }
    }
};
